<!--
  Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->

<br/>
<h1 class="page-header">Create Program Timing</h1>
<br/>
@if (permission()) {
  <div>
  <table style="width:100%">
    <tr>
      <td><h3 style="font-size: x-large; font-weight: bold">{{getPrefix()}} Timing Chart</h3></td>
      <p-confirmDialog key="saveTimingsConfirmation"></p-confirmDialog>
      <td><div class="buttons" style="float: right;">
        <p-select [options]="types" [(ngModel)]="type" (ngModelChange)="onTemplateTypeChange()"
          [disabled]="isTemplateTypeSelectorDisabled()"
          placeholder="Template Type" class="mr-2">
        </p-select>
        @if (type === 'ECS') {
          <p-select [options]="ecsRpoOptions()" [ngModel]="selectedEcsRpo()" (ngModelChange)="onEcsRpoChange($event)"
            [disabled]="loadingMomentumDataSignal()" [filter]="true" [showClear]="true"
            placeholder="Select GPS RPO" class="mr-2">
          </p-select>
          <p-select [options]="ecsModelYearOptions()" [ngModel]="selectedEcsModelYear()" (ngModelChange)="onEcsModelYearChange($event)"
            [disabled]="loadingMomentumDataSignal()" [filter]="true" [showClear]="true"
            placeholder="Select Model Year" class="mr-2">
          </p-select>
          <p-select [options]="ecsProgramOptions()" [ngModel]="selectedEcsProgram()" (ngModelChange)="onEcsProgramChange($event)"
            [disabled]="loadingMomentumDataSignal()" [filter]="true" [showClear]="true"
            optionLabel="projectName" placeholder="Select Program" class="mr-2">
          </p-select>
        } @else {
          <p-select [options]="modelYears" [(ngModel)]="selectedModelYear"
            [disabled]="loadingMomentumDataSignal()" [filter]="true"
            placeholder="Select Model Year" filterBy="year" optionLabel="year" class="mr-2">
          </p-select>
          <p-select [options]="momentumProgramMap.get(selectedModelYear?.year)" [(ngModel)]="selectedMomentumProgram" [showClear]="true" class="mr-2"
            placeholder="Select Program" optionLabel="programName" [filter]="true" filterBy="programName"
            [disabled]="!selectedModelYear || !momentumProgramMap.get(selectedModelYear?.year)?.length || loadingMomentumDataSignal()">
          </p-select>
        }
        <p-button label="Get Program Timing" icon="pi pi-calendar-plus" [loading]="loadingMomentumDataSignal()" class="gm-btn-primary mr-2"
          [disabled]="!canLoadProgramTiming()" (click)="getMomentumProgramByMomentumId();">
        </p-button>
        <p-button label="Save Program Timing" icon="pi pi-save" [loading]="saving" class="gm-btn-primary mr-2"
          [disabled]="programNamesToSave() === ''" (click)="saveProgramTiming();">
        </p-button>
        <p-button label="Clear Programs" [disabled]="!hasSelectedMomentumPrograms()"
        [severity]="!hasSelectedMomentumPrograms() ? 'secondary' : 'warn'" (click)="clearPrograms()" class="mr-2" icon="pi pi-replay"></p-button>
        <p-splitbutton icon="pi pi-download" [disabled]="!hasSelectedMomentumPrograms() || exportingReport()"
          [severity]="!hasSelectedMomentumPrograms() ? 'secondary' : null"
          label="Export Report" class="mr-2"
        [model]="downloadButtonOptions"></p-splitbutton>
        <p-splitbutton icon="pi pi-filter" [disabled]="!hasSelectedMomentumPrograms()"
          [severity]="!hasSelectedMomentumPrograms() ? 'secondary' : null"
          label="{{filterDuration > 0 ? 'Within ' + filterDuration + ' Days' : '30/60/90'}}" class="mr-2"
        [model]="durationButtonOptions"></p-splitbutton>
      </div></td>
    </tr>
  </table>
  @if (exportingReport()) {
    <p-progressBar class="kts-export-progress" mode="indeterminate"></p-progressBar>
  }

  @if (ktsTimingTemplateRowMapSignal().get(type)) {
    <p-tabs [value]="activeTabIndexSignal()" (valueChange)="setActiveTabIndex(+$event); handleTabClick()">
      <p-tablist>
        @for (entry of ktsTimingTemplateRowMapSignal().get(type) | keyvalue; track entry; let i = $index) {
          <p-tab [value]="i">
            <span>
              <i class="pi pi-calendar"></i>
              Version {{entry.key}} ({{ktsTimingTemplateRowMapSignal().get(type)?.get(entry.key)[0].programData.size}})
            </span>
          </p-tab>
        }
        @if (hasSelectedMomentumPrograms()) {
          <p-tab [value]="ktsTimingTemplateRowMapSignal().get(type).size">
            <span>
              <i class="pi pi-calendar-minus"></i>
              Gantt Chart
            </span>
          </p-tab>
        }
      </p-tablist>
      <p-tabpanels>
        @for (entry of ktsTimingTemplateRowMapSignal().get(type) | keyvalue; track entry; let i = $index) {
          <p-tabpanel [value]="i">
            <p>
              <ag-grid-angular style="width: 100%; height: 80vh;" class="ag-theme-balham key-task-scheduler"
                [paginationPageSize]="paginationPageSize" [rowHeight]="30"
                (columnVisible)="onColumnVisible($event, entry.key)"
                [paginationPageSizeSelector]="paginationPageSizeSelector"
                [columnDefs]="fullCols.get(type)?.get(entry.key)"
                [rowData]="ktsTimingTemplateRowMapSignal().get(type)?.get(entry.key)" [pagination]="true"
                [autoSizeStrategy]="autoSizeStrategy" [rowClassRules]="rowClassRules"
                [defaultColDef]="defaultColDef" [enableBrowserTooltips]="true"
                (cellEditingStopped)="onCellEditingStopped($event)"
              (gridReady)="onGridReady($event, entry.key)"></ag-grid-angular>
            </p>
          </p-tabpanel>
        }
        @if (hasSelectedMomentumPrograms()) {
          <p-tabpanel [value]="ktsTimingTemplateRowMapSignal().get(type).size">
            <div style="width: 90%; height: 600px; overflow: hidden; position: relative;">
              @if (activeTabIndexSignal() === ktsTimingTemplateRowMapSignal().get(type).size) {
                <app-gantt-chart app-gantt-chart
                [ganttChart]="ganttChart"></app-gantt-chart>
              }
            </div>
          </p-tabpanel>
        }
      </p-tabpanels>
    </p-tabs>
  }
  <div style="display: block; width: 1px; height: 1px; overflow: hidden;">
    <div style="display: block; width: 1000px; height: 1000px; overflow: hidden;">
      <div #imageExportElement>
        @if (exportingGantt) {
          <app-gantt-chart #exportGantt app-gantt-chart [ganttChart]="ganttChart" trueSize="true"/>
        }
      </div>
    </div>
  </div>
  </div>
}






